import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

// Function to get CSRF token from cookies
const getCsrfToken = () => {
  const name = 'csrftoken';
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for CSRF cookies
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add CSRF token for non-GET requests
    if (config.method !== 'get') {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      }
    }

    // Add auth token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('authToken');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Services API
export const servicesAPI = {
  // Public endpoints
  getCategories: () => api.get('/services/categories/'),
  getServices: (params) => api.get('/services/services/', { params }),
  getAllServices: () => api.get('/services/services/all/'),
  getService: (slug) => api.get(`/services/services/${slug}/`),
  getFeaturedServices: () => api.get('/services/services/featured/'),
  getTestimonials: (params) => api.get('/services/testimonials/', { params }),
  getFeaturedTestimonials: () => api.get('/services/testimonials/featured/'),

  // Admin endpoints for Services
  getAdminServices: () => api.get('/services/services/admin_list/'),
  createService: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (key === 'inclusions') {
        formData.append(key, JSON.stringify(data[key]));
      } else if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    return api.post('/services/services/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateService: (slug, data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (key === 'inclusions') {
        formData.append(key, JSON.stringify(data[key]));
      } else if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    return api.patch(`/services/services/${slug}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteService: (slug) => api.delete(`/services/services/${slug}/`),
  reorderService: (slug, order) => api.post(`/services/services/${slug}/reorder/`, { order }),
};

// Bookings API
export const bookingsAPI = {
  createBooking: (data) => api.post('/bookings/bookings/', data),
  getBooking: (reference) => api.get(`/bookings/bookings/${reference}/`),
  getSlots: (params) => api.get('/bookings/slots/', { params }),
  getAvailableSlots: (date) => api.get('/bookings/slots/available/', { params: { date } }),
};

// Content API
export const contentAPI = {
  getPages: () => api.get('/content/pages/'),
  getPage: (slug) => api.get(`/content/pages/${slug}/`),
  getHeroBanners: () => api.get('/content/hero-banners/'),
  getFeatures: () => api.get('/content/features/'),
  getAnnouncements: () => api.get('/content/announcements/'),
  getPromotions: () => api.get('/content/promotions/'),
};

// Core API
export const coreAPI = {
  getSiteInfo: () => api.get('/core/site-info/'),
};

// Contact API
export const contactAPI = {
  sendMessage: (data) => api.post('/contact/messages/', data),
};

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/api-auth/login/', credentials),
  logout: () => api.post('/api-auth/logout/'),
  getCurrentUser: () => api.get('/api-auth/user/'),
};

export default api;
